import * as React from 'react';
import FormEditingHandler from 'Containers/FormEditingHandler';
import DocumentHeader from 'Components/DocumentHeader';
import DocumentWrapper from 'Components/DocumentWrapper';
import HistoryRow from 'Components/HistoryRow';
import CollapseElem from 'Components/Collapse';
import HistoryRowControls from 'Components/HistoryRowControls';
import { sortPartialDate } from 'neuro-utils';
import HistoryRowData from './HistoryRowData';
import LifestyleFormDynamic from './FormDynamic';
import DocumentCreationButton from 'Components/DocumentCreationButton';
import { useDispatch, useSelector } from 'react-redux';
import { actions } from 'Store/myapp/actions';
import StandAloneFormEditingHandler from 'Containers/StandAloneFormEditingHandler';
import { formatPartialDate } from 'Utility/date';

const Lifestyle = ({ documents }: IOwnProps): React.JSX.Element => {
  const myDocuments = useSelector((s: IState) => s.myapp.sortedAndMergedDocuments).filter(
    d => d._type === 'parkinsonLifestyle',
  );

  const sortedAllLifestyles = [...documents, ...myDocuments].sort((n1, n2) => sortPartialDate(n2.date, n1.date));
  const first = sortedAllLifestyles[0];
  const restArr = sortedAllLifestyles.slice(1);

  const isLifestyleMobile = (d: IControlProps): d is IDocumentWithTypeV2<'parkinsonLifestyle'> => {
    if (myDocuments.some(myDocument => myDocument._id === d._id)) return true;
    return false;
  };

  const dispatch = useDispatch();

  // My document handling
  const [lifestyleMobileOpen, setLifestyleMobileOpen] = React.useState<
    { doc: IDocumentWithTypeV2<'parkinsonLifestyle'>; type: 'edit' | 'view' } | undefined
  >(undefined);
  const [tempLifestyleMobile, setTempLifestyleMobile] = React.useState<
    IDocumentWithTypeV2<'parkinsonLifestyle'> | undefined
  >(undefined);

  const [saving, setSaving] = React.useState<boolean>(false);

  const handleMyLifestyleDelete = async (myDocId: string) => {
    await actions.deleteDocument('parkinsonLifestyle', myDocId, dispatch);
  };

  const saveMyLifestyle = async () => {
    setSaving(true);
    if (lifestyleMobileOpen?.doc._type && lifestyleMobileOpen.doc._id) {
      await actions.putNewCommit(
        lifestyleMobileOpen.doc._type,
        lifestyleMobileOpen.doc._id,
        tempLifestyleMobile,
        dispatch,
      );
    }
    setSaving(false);
    setLifestyleMobileOpen(undefined);
  };

  const handleLifestyleMobileFormChange = (values: TOnChangeValues): void => {
    const name = Object.keys(values)[0];
    if (!name) return;
    const value = values[name];
    if (name) {
      setTempLifestyleMobile(prev => ({
        ...(prev as IDocumentWithTypeV2<'parkinsonLifestyle'>),
        [name]: value,
      }));
    }
  };

  React.useEffect(() => {
    if (lifestyleMobileOpen && Boolean(lifestyleMobileOpen.doc)) {
      setTempLifestyleMobile(structuredClone(lifestyleMobileOpen.doc));
    } else if (!lifestyleMobileOpen) {
      setTempLifestyleMobile(undefined);
    }
  }, [lifestyleMobileOpen]);

  return (
    <React.Fragment>
      {lifestyleMobileOpen ? (
        <StandAloneFormEditingHandler
          currentPageName="lifestyle"
          headerLocaleId={'lifestyle.title'}
          loading={saving}
          cancelEditingAction={() => {
            setLifestyleMobileOpen(undefined);
            setTempLifestyleMobile(undefined);
          }}
          saveAction={() => {
            setSaving(true);
            void saveMyLifestyle();
          }}
          viewing={lifestyleMobileOpen.type === 'view'}
          endViewingAction={() => setLifestyleMobileOpen(undefined)}
        >
          {tempLifestyleMobile ? (
            <LifestyleFormDynamic
              formData={{ document: tempLifestyleMobile, onChange: handleLifestyleMobileFormChange }}
              documents={sortedAllLifestyles}
            />
          ) : (
            <></>
          )}
        </StandAloneFormEditingHandler>
      ) : (
        <FormEditingHandler name="lifestyle" documents={sortedAllLifestyles}>
          {(editing, startEdit, formData): React.JSX.Element => {
            const startEditFunc =
              (document: IDocumentWithTypeV2<'lifestyle' | 'parkinsonLifestyle'> | {}) => (e?: React.MouseEvent) => {
                if (!('_id' in document)) {
                  startEdit(document, 'lifestyle')(e);
                  return;
                }

                if (isLifestyleMobile(document)) {
                  setLifestyleMobileOpen({
                    doc: document,
                    type: 'edit',
                  });
                  return;
                }

                startEdit(document, 'lifestyle')(e);
              };
            return (
              <DocumentWrapper>
                <DocumentHeader
                  name={'lifestyle'}
                  headerId={'lifestyle.title'}
                  editing={editing}
                  editButtons={
                    <div>
                      <DocumentCreationButton name="lifestyle" text={'general.new'} onClick={startEdit({})} />
                    </div>
                  }
                />

                {editing ? <LifestyleFormDynamic formData={formData} documents={sortedAllLifestyles} /> : undefined}

                {!editing && first && sortedAllLifestyles.length > 0 ? (
                  <>
                    <HistoryRow
                      headerText={first.date ? formatPartialDate(first.date) : undefined}
                      key={first._id}
                      rowButton={
                        <HistoryRowControls
                          document={first}
                          startEdit={startEditFunc}
                          enableButtons={isLifestyleMobile(first)}
                          deleteHandler={
                            isLifestyleMobile(first) ? () => handleMyLifestyleDelete(first._id) : undefined
                          }
                        />
                      }
                      controlsMargin={false}
                    >
                      <HistoryRowData d={first} docs={sortedAllLifestyles} startEdit={startEdit} />
                    </HistoryRow>
                    {sortedAllLifestyles.length > 1 && (
                      <CollapseElem
                        localeIDs={{
                          showMessage: 'lifestyle.showPastDocuments',
                          hideMessage: 'lifestyle.hidePastDocuments',
                        }}
                        amount={restArr.length}
                      >
                        {restArr.map(d => (
                          <HistoryRow
                            headerText={d.date ? formatPartialDate(d.date) : undefined}
                            key={d._id}
                            rowButton={
                              <HistoryRowControls
                                document={d}
                                startEdit={startEdit}
                                enableButtons={isLifestyleMobile(d)}
                                deleteHandler={isLifestyleMobile(d) ? () => handleMyLifestyleDelete(d._id) : undefined}
                              />
                            }
                            controlsMargin={false}
                          >
                            <HistoryRowData d={d} docs={sortedAllLifestyles} startEdit={startEdit} />
                          </HistoryRow>
                        ))}
                      </CollapseElem>
                    )}
                  </>
                ) : undefined}
              </DocumentWrapper>
            );
          }}
        </FormEditingHandler>
      )}
    </React.Fragment>
  );
};

interface IOwnProps {
  documents: IDocumentWithTypeV2<'lifestyle'>[];
}

export default Lifestyle;
